import React from 'react';
import BackArrow from '../../components/ui/BackArrow';
import Button from '../../components/ui/Button';
import StatusBadge from '../../components/StatusBadge';
import { MoreMenu, type MoreMenuItem } from './MoreMenu';

/**
 * The client invoice's one sticky header (D98.2).
 *
 * Presentational only. The page hands in what it already computed - its handlers, its permission
 * and status gates, and the figures from its own summary - so nothing here works out money or
 * decides who may act. `save` / `primary` are null when the page's gate hides them.
 */
export interface HeaderAction {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  title?: string;
}

interface InvoiceHeaderProps {
  invoiceNumber: string;
  status?: string;
  isAdditional?: boolean;
  /** The module info button, built by the page. */
  info: React.ReactNode;
  /** Edits not yet saved: shown beside Save. */
  unsavedChanges?: boolean;
  /** D183.6: the page's refresh status, derived from its read facts. `text` says which part is stale, `tone` colours
   *  it. Refresh does what a background tick does. */
  refresh?: { text: string; tone: 'ok' | 'skipped' | 'failed'; busy: boolean; onClick: () => void };
  /** Click on the back link; the page may stop it to ask first. */
  onBack?: (e: React.MouseEvent) => void;
  save: HeaderAction | null;
  primary: HeaderAction | null;
  moreItems: MoreMenuItem[];
  client: string;
  vessel: string;
  /** Already formatted for display. */
  invoiceDate: string;
  clientPo: string;
  /** D183.5 order info, read-only; each shows only when set. */
  port?: string;
  estDelivery?: string;
  /** The page's currency select, unchanged. */
  currencyControl: React.ReactNode;
  chain: React.ReactNode;
  /** Already formatted from the page's own summary. */
  total: string;
  /** D183.3: the ACTUAL margin % (billed and entered lines only), from the page's one order-progress figure; null = "--". */
  marginPct: number | null | undefined;
  /** Every supplier bill is in: the margin is final. */
  marginFinal?: boolean;
  /** D183.3: the order-progress strip, built by the page from the same figure. */
  progress?: React.ReactNode;
  /** Present only when credit notes exist. */
  netAfterCredits?: string;
  /** D146 (item 14): a second, independent badge — "credited" / "partially credited" — read
   *  next to the lifecycle status, never instead of it. Null while no credit note counts. */
  creditStatus?: 'credited' | 'partially credited' | null;
  /** The page measures the header (the line drawer starts below it). */
  containerRef?: React.Ref<HTMLDivElement>;
}

const Field: React.FC<{ label: string; children: React.ReactNode; truncate?: string }> = ({ label, children, truncate }) => (
  <span className={truncate ? `min-w-0 max-w-[22rem] truncate ${truncate}` : undefined} title={truncate ? String(children) : undefined}>
    {label} <b className="font-medium text-slate-900">{children}</b>
  </span>
);

export const InvoiceHeader: React.FC<InvoiceHeaderProps> = (p) => (
  <div
    data-testid="invoice-header"
    ref={p.containerRef}
    className="sticky top-0 z-20 shrink-0 border-b border-slate-200 bg-white px-4 py-3 sm:px-8 no-print flex flex-col gap-2"
  >
    <div className="flex flex-wrap items-center gap-3">
      <BackArrow label="Back to client invoices" to="/client-invoices" onClick={p.onBack} />
      <h1 className="text-xl font-semibold leading-7 text-slate-900">{p.invoiceNumber}</h1>
      {p.status ? <StatusBadge status={p.status} /> : null}
      {/* D146 (item 14): a second badge, not a replacement — the lifecycle status above still
          says draft/sent/paid/cancelled; this says whether a credit note has since reduced it. */}
      {p.creditStatus ? <StatusBadge status={p.creditStatus} tone="attention" /> : null}
      {p.isAdditional ? (
        <span className="inline-flex items-center rounded-full border border-orange-200 bg-orange-50 px-2 py-1 text-xs font-medium leading-none text-orange-800">
          Additional
        </span>
      ) : null}
      {p.info}
      <span className="flex-1" />
      {p.refresh ? (
        <span className="flex flex-col items-end text-xs leading-4" data-testid="invoice-refresh-status">
          <span className={p.refresh.tone === 'failed' ? 'text-red-700' : p.refresh.tone === 'skipped' ? 'text-amber-700' : 'text-slate-500'}>{p.refresh.text}</span>
        </span>
      ) : null}
      {p.refresh ? (
        <Button onClick={p.refresh.onClick} disabled={p.refresh.busy} title="Re-read the order progress and, when you have no unsaved changes, the invoice">
          Refresh
        </Button>
      ) : null}
      {p.unsavedChanges ? <span className="text-sm font-medium text-amber-700">Unsaved changes</span> : null}
      {p.save ? (
        <Button onClick={p.save.onClick} disabled={p.save.disabled} title={p.save.title}>
          {p.save.label}
        </Button>
      ) : null}
      {p.primary ? (
        <Button variant="primary" onClick={p.primary.onClick} disabled={p.primary.disabled} title={p.primary.title}>
          {p.primary.label}
        </Button>
      ) : null}
      <MoreMenu items={p.moreItems} />
    </div>

    <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-sm text-slate-500">
      <Field label="Client" truncate="inline-block align-bottom">{p.client}</Field>
      <Field label="Vessel">{p.vessel}</Field>
      <Field label="Invoice date">{p.invoiceDate}</Field>
      <Field label="Client PO">{p.clientPo}</Field>
      {p.port ? <Field label="Port">{p.port}</Field> : null}
      {p.estDelivery ? <Field label="Est. delivery">{p.estDelivery}</Field> : null}
      {p.currencyControl}
      <span className="flex-1" />
      <Field label="Total">
        <span className="tabular-nums">{p.total}</span>
      </Field>
      <Field label="Actual margin">
        <span className="tabular-nums">{p.marginPct == null ? '--' : `${p.marginPct}%${p.marginFinal ? ' (final)' : ''}`}</span>
      </Field>
      {p.netAfterCredits != null ? (
        <Field label="Net after credits">
          <span className="tabular-nums">{p.netAfterCredits}</span>
        </Field>
      ) : null}
    </div>

    {p.progress ? <div>{p.progress}</div> : null}

    {/* The chain rail renders nothing when no other document is linked; an empty row then folds away. */}
    <div className="text-sm text-slate-500 empty:hidden">{p.chain}</div>
  </div>
);
